import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Avatar,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "redux/store";
import { getDirectors, deleteDirector } from "redux/actions";
import { Director } from "types/models/investorDeskMaster/InvestorDeskMasterApp";
import DirectorDialog from "./DirectorDialog";

const OurTeamPage: React.FC = () => {
  const dispatch = useDispatch();

  const { directors } = useSelector<AppState, AppState["investorDeskMaster"]>(
    ({ investorDeskMaster }) => investorDeskMaster
  );

  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<"all" | "directors" | "heads">("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDirector, setSelectedDirector] = useState<Director | null>(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [directorToDelete, setDirectorToDelete] = useState<Director | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const loadData = async () => {
    setIsLoading(true);
    try {
      await (dispatch as any)(getDirectors());
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || "Failed to load team data",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleAddMember = () => {
    setSelectedDirector(null);
    setDialogOpen(true);
  };

  const handleEditMember = (director: Director) => {
    setSelectedDirector(director);
    setDialogOpen(true);
  };

  const handleDeletePrompt = (director: Director) => {
    setDirectorToDelete(director);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!directorToDelete) return;
    try {
      setIsDeleting(true);
      await (dispatch as any)(deleteDirector(directorToDelete.id));
      setSnackbar({
        open: true,
        message: `${directorToDelete.name} deleted successfully`,
        severity: "success",
      });
      setDeleteConfirmOpen(false);
      setDirectorToDelete(null);
      loadData();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || "Failed to delete team member",
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredMembers = useMemo(() => {
    return (directors || []).filter((member) => {
      // Filter by category
      if (filterType === "directors" && !member.is_director) return false;
      if (filterType === "heads" && member.is_director) return false;

      // Filter by search
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesName = (member.name || "").toLowerCase().includes(query);
        const matchesPost = (member.post || "").toLowerCase().includes(query);
        return matchesName || matchesPost;
      }

      return true;
    });
  }, [directors, filterType, searchQuery]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 50,
              height: 50,
              borderRadius: "12px",
              color: "primary.main",
            }}
          >
            <GroupsIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
             <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.4rem", md: "1.75rem" },
              letterSpacing: "-0.02em",
            }}
          >
              Our Team
            </Typography>
            <Typography
          variant="body2"
        >
              Manage team members, directors, and department heads
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <IconButton onClick={loadData} disabled={isLoading} color="primary" title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddMember}
            sx={{ borderRadius: "8px", textTransform: "none", px: 3, py: 1 }}
          >
            Add Team Member
          </Button>
        </Box>
      </Box>
      <Card sx={{ mb: 4, borderRadius: "12px", boxShadow: 1 }}>
        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <ToggleButtonGroup
              value={filterType}
              exclusive
              onChange={(_, value) => {
                if (value) setFilterType(value);
              }}
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  px: 2.5,
                  fontWeight: 500,
                },
              }}
            >
              <ToggleButton value="all">
                All Members ({directors ? directors.length : 0})
              </ToggleButton>
              <ToggleButton value="directors">
                Directors ({(directors || []).filter((d) => d.is_director).length})
              </ToggleButton>
              <ToggleButton value="heads">
                Department Heads ({(directors || []).filter((d) => !d.is_director).length})
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              size="small"
              placeholder="Search by name or designation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Loading state */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
          <CircularProgress />
        </Box>
      ) : filteredMembers.length === 0 ? (
        /* Empty State */
        <Card sx={{ borderRadius: "16px", p: 8, textAlign: "center", boxShadow: 1 }}>
          <PersonOutlineIcon sx={{ fontSize: 72, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            No team members found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchQuery
              ? "No team members matched your search query"
              : "Get started by adding the first member to your team."}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddMember}
            sx={{ borderRadius: "8px", textTransform: "none" }}
          >
            Add Team Member
          </Button>
        </Card>
      ) : (
        /* Team Members Grid matching sar_cms design */
        <Grid container spacing={3.5}>
          {filteredMembers.map((member) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={member.id}>
              <Card
                sx={{
                  borderRadius: "16px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  transition: "transform 0.25s, box-shadow 0.25s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
                  },
                }}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    p: 3,
                    pt: 4,
                  }}
                >
                  {/* Avatar */}
                  <Avatar
                    src={member.photo_url || member.photo || undefined}
                    alt={member.name}
                    sx={{
                      width: 120,
                      height: 120,
                      mb: 2.5,
                      boxShadow: 2,
                      border: "3px solid #f0f0f0",
                      bgcolor: "primary.main",
                      fontSize: 44,
                    }}
                  >
                    {!member.photo_url && !member.photo && member.name
                      ? member.name.charAt(0).toUpperCase()
                      : null}
                  </Avatar>

                  {/* Category chip */}
                  <Chip
                    label={member.is_director ? "Director" : "Department Head"}
                    size="small"
                    color={member.is_director ? "primary" : "default"}
                    sx={{
                      mb: 1.5,
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />

                  {/* Name */}
                  <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.25, mb: 0.5 }}>
                    {member.name}
                  </Typography>

                  {/* Designation */}
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                    {member.post}
                  </Typography>

                  {/* Short Bio / Description if available */}
                  {member.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: 1.4,
                      }}
                      title={member.description}
                    >
                      {member.description}
                    </Typography>
                  )}
                </CardContent>

                <Divider />

                {/* Bottom Action Bar */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    px: 1,
                    py: 0.5,
                    bgcolor: "grey.50",
                  }}
                >
                  <Button
                    size="small"
                    startIcon={<EditIcon fontSize="small" />}
                    onClick={() => handleEditMember(member)}
                    sx={{ textTransform: "none", color: "text.secondary", flex: 1 }}
                  >
                    Edit
                  </Button>
                  <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
                  <Button
                    size="small"
                    startIcon={<DeleteOutlineIcon fontSize="small" />}
                    onClick={() => handleDeletePrompt(member)}
                    sx={{ textTransform: "none", color: "error.main", flex: 1 }}
                  >
                    Delete
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add / Edit Dialog */}
      <DirectorDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={() => {
          setSnackbar({
            open: true,
            message: selectedDirector
              ? "Team member updated successfully"
              : "Team member added successfully",
            severity: "success",
          });
          loadData();
        }}
        director={selectedDirector}
      />

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle sx={{ fontWeight: "bold" }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            <strong>{directorToDelete?.name}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={isDeleting}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OurTeamPage;
